import React, { useState } from 'react'
import { useHistory } from 'react-router'
import { Formik, FormikProps } from 'formik'
import { Button, ModalInjectedProps } from '@sketch/components'
import {
  useCreateWorkspaceProfileMutation,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { routes } from '@sketch/modules-common'
import {
  Modal,
  ModalBody,
  OrderedBreadcrumbs,
  InfoNoteContainer,
  InformationIcon,
} from './ProfileModal.styles'

import { IntroductionStep } from './IntroductionStep'
import { ProfileDetailsStep } from './ProfileDetailsStep'
import { SelectDocumentsStep } from './SelectDocumentsStep'
import { useProfileShortURL } from '../../hooks/useProfileShortURL'
import { initialValues, validationSchema } from './form'

const BREADCRUMBS = [
  { content: 'Public Profile' },
  { content: 'Details' },
  { content: 'Documents' },
]

interface ProfileModalProps extends ModalInjectedProps {
  workspace: WorkspaceMinimalFragment
  shortUrlName?: string
}

export const ProfileModal = ({
  workspace,
  shortUrlName,
  hideModal,
}: ProfileModalProps) => {
  const history = useHistory()
  const [step, setStep] = useState(0)
  const { currentShortUrl } = useProfileShortURL(shortUrlName || '')

  const [createWorkspaceProfile, { loading }] =
    useCreateWorkspaceProfileMutation({
      onError: 'show-toast',
      onCompleted: () => {
        history.push(
          routes.WORKSPACE_PROFILE_EDIT.create({
            workspaceId: workspace.identifier,
          })
        )
      },
    })

  const prevStep = () => {
    setStep(prev => prev - 1)
  }

  const nextStep = () => {
    setStep(prev => prev + 1)
  }

  const renderStep = (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0:
        return <IntroductionStep />
      case 1:
        return (
          <ProfileDetailsStep workspace={workspace} formikBag={formikBag} />
        )
      case 2:
        return (
          <SelectDocumentsStep workspace={workspace} formikBag={formikBag} />
        )
    }
  }

  const handleSecondaryButtonAction = () => {
    if (step === 0) {
      hideModal()
    } else {
      prevStep()
    }
  }

  const handleSubmitButtonAction = async (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0: {
        nextStep()
        break
      }
      case 1: {
        const { shares, ...stepOneValues } = initialValues
        const errors = formikBag.validateForm()
        formikBag.setTouched(
          Object.keys(stepOneValues).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {} as Record<string, boolean>
          )
        )

        if (Object.keys(errors).length === 0) {
          nextStep()
        }
        break
      }
      case 2: {
        const { values } = formikBag
        const { selectAll, ...formValues } = values

        createWorkspaceProfile({
          variables: {
            workspaceIdentifier: workspace.identifier,
            input: formValues,
          },
        })
        break
      }
    }
  }

  const getSubmitButtonText = () => {
    switch (step) {
      case 0:
        return 'Get Started'
      case 1:
        return 'Choose Documents'
      case 2:
        return 'Create Public Profile'
    }
  }

  const renderFooterNote = () => {
    if (step === 2) {
      return (
        <InfoNoteContainer>
          <InformationIcon />
          You can preview your profile before publishing it.
        </InfoNoteContainer>
      )
    }
  }

  return (
    <Formik
      initialValues={{
        ...initialValues,
        shortUrlName: currentShortUrl,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      // eslint-disable-next-line no-console
      onSubmit={console.log}
    >
      {formikBag => (
        <Modal onCancel={hideModal}>
          <ModalBody>
            {/*
              TODO: Improve breadcrumbs for smaller resolutions
              https://linear.app/sketch/issue/SWEB-1438
              */}
            <OrderedBreadcrumbs
              currentCrumb={step}
              crumbs={BREADCRUMBS}
              showAllInMobile
            />

            {renderStep(formikBag)}
          </ModalBody>
          <Modal.Footer leftContent={renderFooterNote()}>
            <Button
              variant="secondary"
              disabled={loading}
              size="40"
              onClick={() => handleSecondaryButtonAction()}
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              variant="primary"
              disabled={loading}
              loading={loading}
              size="40"
              onClick={() => handleSubmitButtonAction(formikBag)}
            >
              {getSubmitButtonText()}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}
